import { Injectable, Inject, Optional, InjectionToken } from '@angular/core';
import { PlatformInformationService } from '../../../../projects/platform-manager/src/public_api';
import { DOCUMENT } from '@angular/common';
import { LocalStorageService } from './local-storage.service';

declare let gtag: (...parameters) => void;

export const BROWSER_WINDOW = new InjectionToken<Window>('Browser Window', {
  providedIn: 'root',
  factory: () => (typeof window !== 'undefined' ? window : null)
});

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  injected = false;
  private storageHandle = 'unpushedExperiments';
  private unpushedExperiments = this.localStorageService.get<Array<{ id: string; variant: string | number }>>(
    this.storageHandle,
    []
  );
  private unpushedTrackerData: Array<{ type: string; params?: object | string }> = [];

  constructor(
    @Optional() @Inject(BROWSER_WINDOW) private window,
    @Optional() @Inject(DOCUMENT) private document,
    private platformService: PlatformInformationService,
    private localStorageService: LocalStorageService
  ) {}

  /**
   * @param experimentId the experiment id from optimize backend
   * @param variant the variant starting with 0 for regular, 1 for variant 1, ...
   */
  activateExperiment(experimentId: string, variant: string | number, alsoPushUnpushed = true) {
    try {
      if (this.window && typeof this.window.ga !== 'undefined') {
        // unpushed experiments? push and clear!
        if (alsoPushUnpushed) {
          this.unpushedExperiments.forEach(exp => {
            this.window.ga('event', exp.variant, { event_category: 'ABX', label: exp.id }); // <- GA4 reporting test
            this.window.ga('send', {
              // <- old 360 interim reporting test
              hitType: 'event',
              eventCategory: 'ABX',
              eventAction: exp.variant,
              eventLabel: exp.id
            }); // <- old 360 interim reporting test
            this.window.ga('set', 'exp', exp.id + '.' + exp.variant);
            this.window.ga('send', {
              hitType: 'event',
              eventCategory: 'Optimize Server Side',
              eventAction: exp.id,
              eventLabel: exp.variant
            });
          });
          this.unpushedExperiments = [];
          this.localStorageService.remove(this.storageHandle);
        }

        this.window.ga('event', variant, { event_category: 'ABX', label: experimentId }); // <- GA4 reporting test
        this.window.ga('send', {
          // <- old 360 interim reporting test
          hitType: 'event',
          eventCategory: 'ABX',
          eventAction: variant,
          eventLabel: experimentId
        }); // <- old 360 interim reporting test
        this.window.ga('set', 'exp', experimentId + '.' + variant);
        this.window.ga('send', {
          hitType: 'event',
          eventCategory: 'Optimize Server Side',
          eventAction: experimentId,
          eventLabel: variant
        });

        gtag('event', 'abx_test', {
          abx_test_name: experimentId,
          abx_test_version: variant,
          abx_test_combined: experimentId + '_' + variant // where abx_text_combined = skifinder_0 | skifinder_1

          // is skifinder_1 === name: skifinder & version: 1
        });
      } else {
        this.unpushedExperiments.push({ id: experimentId, variant: variant });
        this.localStorageService.set(this.storageHandle, this.unpushedExperiments);
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * used to send tracker data to google analytics like
   * 'send', { hitType: 'event',
   * eventCategory: 'Video',
   * eventAction: 'play',
   * eventLabel: 'cats.mp4'
   * }
   * @param eventType the event 'type' like send or [trackerName].send, etc.
   * @param eventParameters the event data object containing key/value items like hitType: 'event'
   */
  sendData(eventType: string, eventParameters?: object | string) {
    try {
      if (this.window && typeof this.window.ga !== 'undefined') {
        // unpushed data? push and clear!
        if (this.unpushedTrackerData.length) {
          this.unpushedTrackerData.forEach(data => {
            this.window.ga(data.type, data.params);
          });
          this.unpushedTrackerData = [];
        }

        if (eventParameters) {
          this.window.ga(eventType, eventParameters);
        } else {
          this.window.ga(eventType);
        }
      } else {
        if (eventParameters) {
          this.unpushedTrackerData.push({ type: eventType, params: eventParameters });
        } else {
          this.unpushedTrackerData.push({ type: eventType });
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  // TODO: add multivariant test setter

  /**
   * injects analytics code directly into the document head.
   */
  injectGoogleAnalytics() {
    if (!this.injected) {
      this.injected = true;
      if (!this.platformService.isLiveProd()) {
        const node = this.document.createElement('script');
        node.async = true;
        node.src = `https://www.googletagmanager.com/gtag/js?id=${this.platformService.getAnalyticsContainer()}`;

        const node2 = this.document.createElement('script');
        node2.innerHTML = `window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${this.platformService.getAnalyticsContainer()}');
          var isInst = window.matchMedia('(display-mode: standalone)').matches;
          gtag('event', 'pwa_mode', {
            mode: isInst ? 'standalone' : 'browser',
            description: isInst ? 'PWA is running installed' : 'PWA is running in browser'
          })`;
        const head = this.document.getElementsByTagName('head')[0];
        head.insertBefore(node2, head.firstChild);
        head.insertBefore(node, head.firstChild);
      } else {
        const dnsPre = this.document.createElement('link');
        dnsPre.rel = 'dns-prefetch';
        dnsPre.href = 'https://www.google-analytics.com';

        const node = this.document.createElement('script');
        node.type = 'text/javascript';
        const code = `(function (i, s, o, g, r, a, m) {
          i['GoogleAnalyticsObject'] = r; i[r] = i[r] || function () {
            (i[r].q = i[r].q || []).push(arguments)
          }, i[r].l = 1 * new Date(); a = s.createElement(o),
          m = s.getElementsByTagName(o)[0]; a.async = 1; a.src = g; m.parentNode.insertBefore(a, m)
          })(window, document, 'script', '//www.google-analytics.com/analytics.js', 'ga');
          ga('create', '${this.platformService.getAnalyticsContainer()}', 'auto');
          ga('require', 'displayfeatures');
          ga('require', 'GTM-5XPCC97');
          ga('send', 'pageview');
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${this.platformService.getAnalyticsContainer()}');
          var isInst = window.matchMedia('(display-mode: standalone)').matches;
          gtag('event', 'pwa_mode', {
            mode: isInst ? 'standalone' : 'browser',
            description: isInst ? 'PWA is running installed' : 'PWA is running in browser'
          })`;

        node.innerHTML = code;
        node.charset = 'utf-8';
        const head = this.document.getElementsByTagName('head')[0];
        head.insertBefore(node, head.firstChild);
        head.insertBefore(dnsPre, head.firstChild);
        this.unpushedExperiments.forEach(experiment => {
          this.activateExperiment(experiment.id, experiment.variant, false);
        });
        this.localStorageService.remove(this.storageHandle);
        this.unpushedTrackerData.forEach(data => {
          this.sendData(data.type, data.params);
        });
      }
    }
  }
}
